import styled from '@emotion/styled';
import { uiTheme as t, EmptyState, Checkbox } from 'Components/ui';

export interface CourseRow {
  id: string;
  name: string;
  /** Minutos o duración ya formateada; la tabla la pinta tal cual. */
  duration: number | string;
  categoryName: string;
  /** Terminales donde se imparte. Solo lo usan las definiciones que lo tienen. */
  terminalNames?: string[];
}

export interface CoursesTableLabels {
  name: string;
  category: string;
  duration: string;
  empty: string;
  selectAll: string;
  /** Si llega, la tabla añade la columna de terminal. */
  terminal?: string;
}

interface CoursesTableProps {
  rows: CourseRow[];
  labels: CoursesTableLabels;
  /** Ids marcados en esta definición. */
  selected: Set<string>;
  onToggle: (courseId: string) => void;
  onToggleAll: () => void;
}

const GRID = '38px 2.6fr 1.4fr 0.9fr';
const GRID_WITH_TERMINAL = '38px 2.4fr 1.3fr 1.3fr 0.9fr';

const Surface = styled.div({
  marginTop: 20,
  background: t.color.surface,
  border: `1px solid ${t.color.border}`,
  borderRadius: t.radius.lg,
  overflow: 'hidden',
  boxShadow: t.shadow.card,
});

// El reparto de columnas va por `style`: emotion reenviaría al DOM una prop
// `template` y React se quejaría de un atributo desconocido en el div.
const HeadRow = styled.div({
  display: 'grid',
  alignItems: 'center',
  gap: 16,
  padding: '14px 24px',
  background: t.color.tableHeadBg,
  borderBottom: `1px solid ${t.color.rowBorder}`,
});

const HeadCell = styled.span({
  fontFamily: t.font,
  fontSize: 11.5,
  fontWeight: 600,
  letterSpacing: '0.6px',
  color: t.color.tableHeadText,
});

// La fila entera es el objetivo del clic: en una pantalla que solo marca y
// desmarca, obligar a apuntar a una casilla de 16px es trabajo de más.
const BodyRow = styled.label({
  display: 'grid',
  alignItems: 'center',
  gap: 16,
  padding: '15px 24px',
  borderTop: `1px solid ${t.color.rowBorder}`,
  cursor: 'pointer',
  '&:hover': { background: t.color.rowHoverBg },
});

const Name = styled.div({
  fontFamily: t.font,
  fontSize: 14.5,
  fontWeight: 600,
  color: t.color.title,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const Cell = styled.div({
  fontFamily: t.font,
  fontSize: 13.5,
  color: t.color.text,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

/**
 * Organismo: catálogo de cursos con casilla de marcado.
 *
 * La comparten las cuatro pantallas de definición, que responden la misma
 * pregunta repetida por curso: ¿entra o no entra? La columna de terminal solo
 * aparece donde significa algo —departamento y puesto—; en empresa y opcionales
 * el curso vale igual en todas, así que repetiría el mismo valor en cada fila.
 * No hay acciones por fila: lo único que se hace aquí es marcar, y el guardado
 * es uno solo para toda la pantalla.
 */
const CoursesTable = ({ rows, labels, selected, onToggle, onToggleAll }: CoursesTableProps) => {
  const allSelected = rows.length > 0 && rows.every(row => selected.has(row.id));
  const gridTemplateColumns = labels.terminal ? GRID_WITH_TERMINAL : GRID;

  return (
    <Surface>
      <HeadRow style={{ gridTemplateColumns }}>
        <Checkbox checked={allSelected} onChange={onToggleAll} aria-label={labels.selectAll} />
        <HeadCell>{labels.name}</HeadCell>
        {labels.terminal ? <HeadCell>{labels.terminal}</HeadCell> : null}
        <HeadCell>{labels.category}</HeadCell>
        <HeadCell>{labels.duration}</HeadCell>
      </HeadRow>

      {rows.map(row => (
        <BodyRow key={row.id} style={{ gridTemplateColumns }}>
          <Checkbox checked={selected.has(row.id)} onChange={() => onToggle(row.id)} aria-label={row.name} />
          <Name>{row.name}</Name>
          {labels.terminal ? <Cell>{row.terminalNames?.join(', ') || '—'}</Cell> : null}
          <Cell>{row.categoryName}</Cell>
          <Cell>{row.duration}</Cell>
        </BodyRow>
      ))}

      {rows.length === 0 ? <EmptyState message={labels.empty} /> : null}
    </Surface>
  );
};

export default CoursesTable;
